import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getRetainerTranslations } from '@common/i18n/retainer';
import type { RetainerContactAgreementResponse } from '@common/types/retainerAgreements';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { CloseOutlined, CheckCircle } from '@mui/icons-material';
import { Box, Grid, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { useState } from 'react';
import DownloadButton from '@/components/Buttons/DownloadButton';
import DataTable, { type Column } from '@/components/DataTable';
import { getDownloadLink, getFileDetails } from '@/services/FileService';
import { convertUTCDateToEST } from '@/utils';

interface Props {
    rows: RetainerContactAgreementResponse[];
    language?: TemplateFormLanguage;
}

export default function RetainerAcceptanceTable({
    rows,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const theme = useTheme();
    const t = getRetainerTranslations(language);
    const signedDateFormat = language === 'fr' ? 'dd/MM/yyyy' : 'M/d/yyyy';

    const [documentButtonState, setDocumentButtonState] = useState<
        Record<string, 'default' | 'success' | 'loading'>
    >({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const columns: Column<RetainerContactAgreementResponse>[] = [
        {
            id: 'downloadLink',
            label: t.table.downloadDocument,
            align: 'center',
            _render: (row) => {
                return (
                    row.isRetainerSigned && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Link
                                sx={{
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                }}
                                download
                                onClick={() => handleDownload(row.fileId)}
                            >
                                <DownloadButton
                                    iconState={
                                        row.fileId ? documentButtonState[row.fileId] : undefined
                                    }
                                />
                            </Link>
                        </Box>
                    )
                );
            },
        },
        {
            id: 'clientName',
            label: t.table.clientName,
            _render: (row) =>
                `${row.firstName}${row.middleName ? ` ${row.middleName}` : ''} ${row.lastName}`,
        },
        {
            id: 'isRetainerSigned',
            label: t.table.accepted,
            align: 'center',
            _render: (row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <span>
                        {row.isRetainerSigned ? (
                            <CheckCircle sx={{ color: 'green', fontSize: 24 }} />
                        ) : (
                            <CloseOutlined sx={{ color: 'red', fontSize: 24 }} />
                        )}
                    </span>
                </Box>
            ),
        },
        {
            id: 'createdAt',
            label: t.table.signedDate,
            _render: (rowData) => {
                if (!rowData.isRetainerSigned) return null;
                return convertUTCDateToEST(rowData.createdAt, signedDateFormat);
            },
        },
    ];

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchReviewFile = async (fileId: string, fileName?: string) => {
        try {
            const response = await fetch(fileId);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName ?? 'idv-review-report.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleDownload = async (fileId: string | null) => {
        if (!fileId) return;

        setDocumentButtonState((prev) => ({
            ...prev,
            [fileId]: 'loading',
        }));
        try {
            const downloadLink = getDownloadLink(fileId);
            const fileDetails = await getFileDetails(fileId);

            if (fileDetails?.data?.fileName) {
                fetchReviewFile(downloadLink, fileDetails.data.fileName);
            }
            setDocumentButtonState((prev) => ({
                ...prev,
                [fileId]: 'success',
            }));
        } catch (err) {
            console.error('Error fetching file details:', err);
            setDocumentButtonState((prev) => ({
                ...prev,
                [fileId]: 'default',
            }));
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
                <Typography
                    sx={{
                        marginBottom: '16px',
                        marginLeft: '24px',
                    }}
                    variant="h2"
                >
                    {t.signatureStatusTitle}
                </Typography>
                <Box
                    sx={{
                        marginLeft: '24px',
                        marginRight: '24px',
                    }}
                >
                    <DataTable
                        generateKeyFrom={'contactId'}
                        columns={columns}
                        rows={rows}
                        customStyles={{
                            border: `1px solid ${theme.palette.neutral.borderColor}`,
                            boxShadow: 'none',
                        }}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Box>
            </Grid>
        </Grid>
    );
}
